import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useLegacyBallAuthorization } from '../hooks/useLegacyBallAuthorization';
import { usePageAuthorization } from '../hooks/usePageAuthorization';
import { DownloadDataTab } from '../components/legacy-ball/DownloadDataTab';
import { LegacyBallInfoView, LegacyBallInfoResponse } from '../components/legacy-ball/LegacyBallInfoView';
import { LegacyBallSettingsView } from '../components/legacy-ball/LegacyBallSettingsView';
import { EventSetupPanel } from '../components/legacy-ball/EventSetupPanel';
import { RegistrationsTab } from '../components/legacy-ball/RegistrationsTab';
import { TableManagementTab } from '../components/legacy-ball/table-management/TableManagementTab';
import { TicketInventoryTab } from '../components/legacy-ball/TicketInventoryTab';
import { TicketSalesProgressCard } from '../components/legacy-ball/TicketSalesProgressCard';
import { GuestManagementView } from '../components/legacy-ball/GuestManagementView';
import { TableReservationRequests } from '../components/legacy-ball/TableReservationRequests';
import { RsvpImportPanel } from '../components/rsvp-import/RsvpImportPanel';
import { RsvpImportHistory } from '../components/rsvp-import/RsvpImportHistory';
import { useEvents } from '../contexts/EventContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { MobileTabBar, MobileDesktopOnly, type MobileTab } from '../components/mobile/MobilePageShell';

type ActiveTab = 'information' | 'setup' | 'settings' | 'registrations' | 'table-management' | 'ticket-inventory' | 'guests' | 'download-data';

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  pageMobile: { padding: '1rem', paddingBottom: 'calc(72px + env(safe-area-inset-bottom))', maxWidth: '100%' },
  headerMobile: { marginBottom: '0.75rem' },
  titleMobile: { fontSize: '1.25rem' },
  eventSwitcherMobile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.3rem',
    marginBottom: '1rem',
  },
  eventSwitcherLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    color: '#6b7280',
  },
  eventSwitcherSelect: {
    width: '100%',
    padding: '0.7rem 0.85rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0f2d1f',
    backgroundColor: '#fff',
    border: '1px solid #1a6b3c',
    borderRadius: '8px',
    cursor: 'pointer',
    appearance: 'none' as const,
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%231a6b3c' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 0.75rem center',
    paddingRight: '2.25rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0' },
  secondaryButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#1a6b3c',
    backgroundColor: '#fff',
    border: '1px solid #1a6b3c',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  tabBar: {
    display: 'flex',
    gap: '0',
    marginBottom: '1.5rem',
    borderBottom: '2px solid #e0e0e0',
  },
  tab: {
    padding: '0.75rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#666',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    transition: 'color 0.2s, border-color 0.2s',
  },
  tabActive: {
    padding: '0.75rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#1a6b3c',
    borderBottom: '2px solid #1a6b3c',
    marginBottom: '-2px',
    transition: 'color 0.2s, border-color 0.2s',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
    fontSize: '1rem',
  },
};

export function LegacyBallPage() {
  const { user } = useAuth();
  const { events, selectedEvent, selectedEventId, selectEvent, enabledModules } = useEvents();
  const isTicketed = enabledModules.includes('ticketing');
  const { isAuthorized } = useLegacyBallAuthorization();
  const { canEdit: pageCanEdit } = usePageAuthorization('legacy-ball');
  const canEdit = isAuthorized || pageCanEdit;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();

  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['information', 'setup', 'settings', 'registrations', 'table-management', 'ticket-inventory', 'guests', 'download-data'].includes(tabParam)) {
      return tabParam as ActiveTab;
    }
    return 'information';
  });
  const [eventInfo, setEventInfo] = useState<LegacyBallInfoResponse | null>(null);
  const [guestSubView, setGuestSubView] = useState<'management' | 'import' | 'table-requests'>(() => {
    // Honor ?sub=import so the setup wizard can deep-link straight to the
    // Guests → Import screen after creating an event.
    const sub = searchParams.get('sub');
    return sub === 'import' || sub === 'table-requests' ? (sub as 'import' | 'table-requests') : 'management';
  });
  const [rsvpRefresh, setRsvpRefresh] = useState(0);
  const [infoError, setInfoError] = useState('');
  const [infoLoading, setInfoLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Re-scope when the selected event changes so switching events in-page
    // (via the mobile event switcher) refreshes the Information tab.
    setInfoLoading(true);
    setInfoError('');

    async function fetchEventInfo() {
      try {
        const data = await apiClient<LegacyBallInfoResponse>('/legacy-ball/info');
        if (!cancelled) {
          setEventInfo(data);
        }
      } catch {
        if (!cancelled) {
          setInfoError('Failed to load event information.');
        }
      } finally {
        if (!cancelled) {
          setInfoLoading(false);
        }
      }
    }

    fetchEventInfo();

    return () => {
      cancelled = true;
    };
  }, [selectedEventId]);

  // The visible tabs, in order, respecting canEdit. Shared by both the mobile
  // scrollable bar and the desktop fixed bar so they never drift.
  const visibleTabs: { key: ActiveTab; label: string }[] = [
    { key: 'information', label: 'Information' },
    ...(canEdit
      ? ([
          { key: 'setup', label: '⚙️ Setup' },
          { key: 'settings', label: 'Settings' },
          { key: 'registrations', label: 'Registrations' },
          { key: 'table-management', label: 'Table Management' },
          { key: 'ticket-inventory', label: 'Ticket Inventory' },
          { key: 'guests', label: '👥 Guests' },
          { key: 'download-data', label: '📥 Download Data' },
        ] as { key: ActiveTab; label: string }[])
      : []),
  ];

  return (
    <div style={{ ...styles.page, ...(isMobile ? styles.pageMobile : null) }}>
      <div style={{ ...styles.header, ...(isMobile ? styles.headerMobile : null) }}>
        <h1 style={{ ...styles.title, ...(isMobile ? styles.titleMobile : null) }}>
          {user && ['CustomerSuperUser', 'CustomerEditor', 'CustomerUser'].includes(user.role)
            ? 'Manage Event'
            : 'Legacy Ball'}
        </h1>
        {user && ['Admin', 'Organizer'].includes(user.role) && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              style={styles.secondaryButton}
              onClick={() => navigate('/qr-generator')}
            >
              🎫 QR Generator
            </button>
            <button
              style={styles.secondaryButton}
              onClick={() => navigate('/registration-linking')}
            >
              Registration Linking
            </button>
          </div>
        )}
      </div>

      {/* In-page event switcher (mobile only) — lets operators change the
          active event without going back to the dashboard. Changing it calls
          selectEvent(), which re-scopes X-Event-Id and refreshes the page. */}
      {isMobile && events.length > 0 && (
        <div style={styles.eventSwitcherMobile}>
          <label htmlFor="lb-event-switcher" style={styles.eventSwitcherLabel}>
            Active event
          </label>
          <select
            id="lb-event-switcher"
            style={styles.eventSwitcherSelect}
            value={selectedEventId ?? ''}
            onChange={(e) => selectEvent(e.target.value || null)}
            aria-label="Change the active event"
          >
            {!selectedEvent && <option value="">Select an event…</option>}
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tab Navigation — scrollable on mobile, fixed row on desktop */}
      {isMobile ? (
        <div style={{ margin: '0 -1rem 1.25rem' }}>
          <MobileTabBar
            tabs={visibleTabs as MobileTab[]}
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k as ActiveTab)}
          />
        </div>
      ) : (
        <div style={styles.tabBar}>
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              style={activeTab === t.key ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'information' && (
        <>
          {infoError && <div style={styles.error}>{infoError}</div>}
          {infoLoading ? (
            <div style={styles.loading}>Loading event information...</div>
          ) : (
            <>
              <TicketSalesProgressCard />
              <div style={{ marginTop: '1.5rem' }}>
                <LegacyBallInfoView data={eventInfo} />
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'setup' && canEdit && (
        <EventSetupPanel />
      )}

      {activeTab === 'settings' && canEdit && (
        <LegacyBallSettingsView user={user} />
      )}

      {activeTab === 'registrations' && canEdit && (
        <RegistrationsTab />
      )}

      {activeTab === 'table-management' && canEdit && (
        isMobile ? (
          <MobileDesktopOnly
            title="Table Management"
            message="The floorplan and seating tools work best on a larger screen. Open this on a desktop to manage tables and seating."
          />
        ) : (
          <TableManagementTab />
        )
      )}

      {activeTab === 'ticket-inventory' && canEdit && (
        isMobile ? (
          <MobileDesktopOnly
            title="Ticket Inventory"
            message="Ticket inventory uses wide data grids best viewed on a desktop. Open this on a larger screen to continue."
          />
        ) : (
          <TicketInventoryTab />
        )
      )}

      {activeTab === 'guests' && canEdit && (
        <>
          <nav style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <button onClick={() => setGuestSubView('management')} style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: guestSubView === 'management' ? '#1a6b3c' : '#f5f5f5', color: guestSubView === 'management' ? '#fff' : '#6b7280', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Guest Management
            </button>
            <button onClick={() => setGuestSubView('import')} style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: guestSubView === 'import' ? '#1a6b3c' : '#f5f5f5', color: guestSubView === 'import' ? '#fff' : '#6b7280', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Import Guests
            </button>
            <button onClick={() => setGuestSubView('table-requests')} style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: guestSubView === 'table-requests' ? '#1a6b3c' : '#f5f5f5', color: guestSubView === 'table-requests' ? '#fff' : '#6b7280', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Table Requests
            </button>
          </nav>
          {guestSubView === 'management' && <GuestManagementView />}
          {guestSubView === 'import' && (
            isTicketed ? (
              <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '1.25rem' }}>
                <h3 style={{ color: '#0f2d1f', fontSize: '1.1rem', margin: '0 0 0.25rem 0' }}>🎟️ Load guests from an order file</h3>
                <p style={{ color: '#666', fontSize: '0.85rem', lineHeight: 1.4, margin: '0 0 1rem 0' }}>
                  This is a ticketed event, so guests come in via your order/ticket file.
                  Use the dedicated importer under Ticket Inventory → Import to upload orders and generate tickets.
                </p>
                <button
                  style={{ background: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
                  onClick={() => setActiveTab('ticket-inventory')}
                >
                  Go to Ticket Inventory → Import →
                </button>
              </div>
            ) : selectedEventId ? (
              <>
                <RsvpImportPanel
                  eventId={selectedEventId}
                  onImported={() => setRsvpRefresh((n) => n + 1)}
                  onReset={() => setRsvpRefresh((n) => n + 1)}
                />
                <RsvpImportHistory
                  eventId={selectedEventId}
                  refreshTrigger={rsvpRefresh}
                  onReversed={() => setRsvpRefresh((n) => n + 1)}
                />
              </>
            ) : (
              <div style={{ color: '#6b7280', padding: '1rem' }}>Select an event to import guests.</div>
            )
          )}
          {guestSubView === 'table-requests' && <TableReservationRequests />}
        </>
      )}

      {activeTab === 'download-data' && canEdit && (
        <DownloadDataTab />
      )}
    </div>
  );
}
